import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  InitiateAuthCommand,
  GlobalSignOutCommand,
  GetUserCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  AuthFlowType,
} from '@aws-sdk/client-cognito-identity-provider';
import { config } from '../config';
import { clearPerUserBrowserStorage } from './storageCleanup';

const client = new CognitoIdentityProviderClient({
  region: config.cognito.region,
});

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface UserInfo {
  userId: string;
  email: string;
  displayName?: string;
  groups?: string[];
}

const TOKEN_KEY = 'auth_tokens';
const USER_KEY = 'user_info';

// In-memory cache for short-lived access/id tokens. The refresh token is
// persisted to localStorage so the session survives a browser restart;
// access/id tokens get rehydrated from refresh on cold start.
let inMemoryAccessToken: string | null = null;
let inMemoryIdToken: string | null = null;
let inMemoryExpiresIn: number | null = null;

// One-time migration: clear any tokens / user_info left behind in sessionStorage
// from the prior sessionStorage-only scheme. Runs once per page load.
function migrateLegacyStorage(): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(TOKEN_KEY) !== null) sessionStorage.removeItem(TOKEN_KEY);
      if (sessionStorage.getItem(USER_KEY) !== null) sessionStorage.removeItem(USER_KEY);
    }
  } catch {
    // ignore — private mode etc.
  }
}
migrateLegacyStorage();

export class AuthService {
  async register(email: string, password: string, displayName: string, tosVersion: string = '1.0', inviteToken?: string): Promise<{ userId: string }> {
    const clientMetadata: Record<string, string> = {};
    
    // Pass invite token via clientMetadata for pre-signup Lambda validation
    if (inviteToken) {
      clientMetadata.inviteToken = inviteToken;
    }
    
    const command = new SignUpCommand({
      ClientId: config.cognito.clientId,
      Username: email,
      Password: password,
      UserAttributes: [
        { Name: 'email', Value: email },
        { Name: 'custom:tosAcceptedVersion', Value: tosVersion },
      ],
      ClientMetadata: clientMetadata,
    });

    const response = await client.send(command);
    
    // Store display name temporarily for profile creation after verification
    sessionStorage.setItem('pendingDisplayName', displayName);
    
    return { userId: response.UserSub || '' };
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    const command = new ConfirmSignUpCommand({
      ClientId: config.cognito.clientId,
      Username: email,
      ConfirmationCode: code,
    });

    await client.send(command);
  }

  async resendConfirmationCode(email: string): Promise<void> {
    const command = new ResendConfirmationCodeCommand({
      ClientId: config.cognito.clientId,
      Username: email,
    });

    await client.send(command);
  }

  async forgotPassword(email: string): Promise<void> {
    const command = new ForgotPasswordCommand({
      ClientId: config.cognito.clientId,
      Username: email.toLowerCase().trim(),
    });

    await client.send(command);
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: config.cognito.clientId,
      Username: email.toLowerCase().trim(),
      ConfirmationCode: code.trim(),
      Password: newPassword,
    });

    await client.send(command);
  }

  async login(email: string, password: string): Promise<AuthTokens> {
    const command = new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: config.cognito.clientId,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    });

    const response = await client.send(command);

    if (!response.AuthenticationResult) {
      throw new Error('Authentication failed');
    }

    const tokens: AuthTokens = {
      accessToken: response.AuthenticationResult.AccessToken || '',
      idToken: response.AuthenticationResult.IdToken || '',
      refreshToken: response.AuthenticationResult.RefreshToken || '',
      expiresIn: response.AuthenticationResult.ExpiresIn || 3600,
    };

    this.storeTokens(tokens);

    // Get user identity from Cognito (sub, email)
    const userInfo = await this.getUserInfo(tokens.accessToken);

    // Fetch display name + verified groups from the backend.
    try {
      const { apiService } = await import('./api');
      const profile = await apiService.getUserProfile();
      if (profile.displayName) {
        userInfo.displayName = profile.displayName;
      }
      userInfo.groups = profile.groups ?? [];
    } catch (error) {
      console.warn('Could not fetch user profile:', error);
    }

    this.storeUserInfo(userInfo);
    // storeUserInfo strips groups before persisting; expose the in-memory
    // copy via getUserInfoFromStorage's caller pattern by stashing groups
    // on a transient property the caller can read.
    return tokens;
  }

  async logout(): Promise<void> {
    const tokens = this.getTokens();

    // Clear tokens and user info immediately to prevent race conditions
    this.clearTokens();
    this.clearUserInfo();

    // Wipe other per-user browser storage so the next user on this device
    // does not inherit stale state (read receipts, cached API responses, etc.).
    // Device-level preferences (e.g. PWA install dismissal) are intentionally kept.
    clearPerUserBrowserStorage();

    // Then attempt to sign out from Cognito
    if (tokens?.accessToken) {
      try {
        const command = new GlobalSignOutCommand({
          AccessToken: tokens.accessToken,
        });
        await client.send(command);
      } catch (error) {
        console.error('Logout error:', error);
        // Even if Cognito sign out fails, local tokens are already cleared
      }
    }
  }

  async getUserInfo(accessToken: string): Promise<UserInfo> {
    const command = new GetUserCommand({
      AccessToken: accessToken,
    });

    const response = await client.send(command);
    const emailAttr = response.UserAttributes?.find((attr) => attr.Name === 'email');
    const subAttr = response.UserAttributes?.find((attr) => attr.Name === 'sub');

    // groups are intentionally NOT derived from the client-side ID token.
    // The only trustworthy source is the backend /users/me response, which
    // reads groups from the API Gateway Cognito authorizer claims.
    return {
      userId: subAttr?.Value || '',
      email: emailAttr?.Value || '',
    };
  }

  // Refresh token persists to localStorage so "remember me" survives browser
  // restarts. Access/id tokens stay in memory only — they expire in ~1h and a
  // cold start re-derives them from the refresh token, which keeps the XSS
  // exfil window for short-lived bearer tokens narrower than localStorage.
  storeTokens(tokens: AuthTokens): void {
    inMemoryAccessToken = tokens.accessToken || null;
    inMemoryIdToken = tokens.idToken || null;
    inMemoryExpiresIn = tokens.expiresIn ?? null;
    if (tokens.refreshToken) {
      localStorage.setItem(TOKEN_KEY, JSON.stringify({ refreshToken: tokens.refreshToken }));
    }
  }

  getTokens(): AuthTokens | null {
    let refreshToken = '';
    try {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { refreshToken?: string };
        refreshToken = parsed.refreshToken || '';
      }
    } catch {
      this.clearTokens();
    }
    if (!inMemoryAccessToken && !refreshToken) return null;
    return {
      accessToken: inMemoryAccessToken || '',
      idToken: inMemoryIdToken || '',
      refreshToken,
      expiresIn: inMemoryExpiresIn ?? 3600,
    };
  }

  clearTokens(): void {
    inMemoryAccessToken = null;
    inMemoryIdToken = null;
    inMemoryExpiresIn = null;
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
  }

  storeUserInfo(userInfo: UserInfo): void {
    // groups are deliberately stripped before caching — the only trustworthy
    // source for `groups` is the backend /users/me response, which derives
    // them from the API Gateway Cognito authorizer claims.
    const { groups: _ignored, ...safe } = userInfo;
    void _ignored;
    localStorage.setItem(USER_KEY, JSON.stringify(safe));
  }

  getUserInfoFromStorage(): UserInfo | null {
    const stored = localStorage.getItem(USER_KEY);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored) as UserInfo;
      // Defensive: never return cached groups even if a stale entry has them.
      delete parsed.groups;
      return parsed;
    } catch {
      this.clearUserInfo();
      return null;
    }
  }

  clearUserInfo(): void {
    localStorage.removeItem(USER_KEY);
  }

  async refreshTokens(): Promise<AuthTokens | null> {
    const tokens = this.getTokens();
    if (!tokens?.refreshToken) return null;

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: config.cognito.clientId,
        AuthParameters: {
          REFRESH_TOKEN: tokens.refreshToken,
        },
      });

      const response = await client.send(command);
      if (!response.AuthenticationResult) return null;

      // Refresh token is not returned on refresh - keep the existing one
      const newTokens: AuthTokens = {
        accessToken: response.AuthenticationResult.AccessToken || '',
        idToken: response.AuthenticationResult.IdToken || '',
        refreshToken: tokens.refreshToken,
        expiresIn: response.AuthenticationResult.ExpiresIn || 3600,
      };

      this.storeTokens(newTokens);
      return newTokens;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }

  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    return !!tokens?.accessToken;
  }

  getAccessToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.accessToken || null;
  }
  getIdToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.idToken || null;
  }

  /**
   * Deprecated — do not use for gating UI. Cached `groups` are no longer
   * persisted (only the backend's verified /users/me response is trustworthy).
   * Always returns false; callers should use `useAuth().isAdmin` which derives
   * from the backend response.
   */
  isUserAdmin(): boolean {
    return false;
  }
}

export const authService = new AuthService();
